import Header from "../components/Header";
import AddIcon from "@mui/icons-material/Add";
import React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Menu, Play, Plus, Trash2, X } from "lucide-react";
import AddPlanModal from "./components/AddPlanModal";
import WeekTimeRangePicker from "./components/WeekTimeRangePicker";
import CalendarPlannerEntryModal from "./components/CalendarPlannerEntryModal";
import PlannerCalendarView from "./components/PlannerCalendarView";
import { useAuth } from "../authentication/AuthContext";
import { useProjects } from "../project/hooks/useProjects";
import { useTask, useUpdateTask } from "../task/hooks/useTasks";
import { useNotes } from "../note/hooks/useNote";
import { useRecallCards } from "../recall/hooks/useRecall";
import { useFolders } from "../folder/hooks/useFolder";
import { useUpsertPlanner, usePlannerById, usePlannerOverviews } from "./hooks/usePlanner";
import { useStats } from "../reports/hooks/useStats";
import { updateWeekBreakdownAfterDrop } from "./utils/plannerCalendar";
import { useNavigate } from "react-router-dom";

const timePeriodLabels = {
    today: "Today",
    tomorrow: "Tomorrow",
    weekly: "This Week",
    nextweek: "Next Week",
    monthly: "This Month",
    nextmonth: "Next Month",
    custom: "Custom",
};

const currentPeriods = ["today", "weekly", "monthly"];
const upcomingPeriods = ["tomorrow", "nextweek", "nextmonth"];
const staticPeriods = [...currentPeriods, ...upcomingPeriods];
const focusItemTypes = ["Task", "Project", "Learning", "Note", "Recall Card", "Custom item"];
const weekdayKeys = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const fullWeekdayKeys = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const monthlyWeekKeys = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"];
const learningObjectiveTypes = ["Note", "Recall Card", "Folder", "Custom"];
const PLANNER_BACKEND_IDS_STORAGE_KEY = "plannerBackendIds";
const weekdayKeyByDayNumber = {
    0: "Sun",
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
};

const createWeekdayMap = () => weekdayKeys.reduce((acc, day) => ({ ...acc, [day]: "" }), {});
const createWeekBreakdownMap = () => fullWeekdayKeys.reduce((acc, day) => ({ ...acc, [day]: [] }), {});
const createMonthlyWeekMap = () => monthlyWeekKeys.reduce((acc, week) => ({ ...acc, [week]: [] }), {});
const createWeekBreakdownInputState = () => fullWeekdayKeys.reduce((acc, day) => ({ ...acc, [day]: { type: "Task", title: "", startTime: "", endTime: "" } }), {});

    const sectionConfigs = {
    today: [
        {
            id: "mainOutcome",
            label: "Main outcome",
            inputType: "text",
            placeholder: "What is your main focus / outcome for today?",
        },
        {
            id: "reviewReflection",
            label: "Notes / Reflection",
            inputType: "textarea",
            placeholder: "What did you complete? What blocked you? What moves to tomorrow?...",
            fullWidth: true,
        },
    ],
    tomorrow: [
        {
            id: "mainOutcome",
            label: "Main outcome",
            inputType: "text",
            placeholder: "What is your main focus / outcome for tomorrow?",
        },
        {
            id: "reviewReflection",
            label: "Notes / Reflection",
            inputType: "textarea",
            placeholder: "What do you plan to complete tomorrow? Any notes...",
            fullWidth: true,
        },
    ],
    daily: [
        { id: "mainFocus", label: "Main focus", inputType: "textarea", placeholder: "Add details..." },
        { id: "tasksToComplete", label: "Action items", inputType: "textarea", placeholder: "Add details..." },
        { id: "sessionsPlanned", label: "Sessions planned", inputType: "textarea", placeholder: "Add details..." },
        { id: "reviewReflection", label: "Review / reflection", inputType: "textarea", placeholder: "Add details..." },
    ],
    weekly: [
        {
            id: "weeklyOutcomes",
            label: "1. Weekly Outcomes",
            inputType: "textarea",
            placeholder: "1-3 outcomes that make this week successful...",
            fullWidth: true,
        },
        {
            id: "weekBreakdown",
            label: "3. Week Breakdown",
            inputType: "weekBreakdown",
            valueType: "dailyListMap",
            placeholder: "Add item...",
            fullWidth: true,
        },
        {
            id: "weeklyReview",
            label: "Weekly review",
            inputType: "textarea",
            placeholder: "How did this week go? Wins, blockers, next steps...",
            fullWidth: true,
        },
    ],
    monthly: [
        { id: "priorities", label: "Monthly priorities", inputType: "textarea", placeholder: "Add details..." },
        {
            id: "projects",
            label: "Key projects",
            inputType: "projectMultiSelect",
            valueType: "list",
            fullWidth: true,
        },
        {
            id: "milestones",
            label: "Milestones",
            inputType: "checklist",
            valueType: "checklist",
            fullWidth: true,
        },
        {
            id: "learning",
            label: "Learning objectives",
            inputType: "learningLinks",
            valueType: "list",
            fullWidth: true,
        },
        {
            id: "monthlyWeeklyBreakdown",
            label: "Weekly breakdown",
            inputType: "monthlyWeeklyBreakdown",
            valueType: "weeklyListMap",
            fullWidth: true,
        },
    ],
    custom: [
        { id: "custom1", label: "Focus Area 1", inputType: "textarea", placeholder: "Add details..." },
    ],
};

const resolveTemplateKey = (period) => {
    if (period === "today") return "today";
    if (period === "tomorrow") return "tomorrow";
    if (period === "nextweek") return "weekly";
    if (period === "nextmonth") return "monthly";
    return period;
};

const buildEmptyPlan = (timePeriod, title = "") => {
    const templateKey = resolveTemplateKey(timePeriod);
    const sections = {};

    (sectionConfigs[templateKey] || sectionConfigs.custom).forEach((section) => {
        if (section.valueType === "list") {
            sections[section.id] = [];
            return;
        }
        if (section.valueType === "dailyMap") {
            sections[section.id] = createWeekdayMap();
            return;
        }
        if (section.valueType === "dailyListMap") {
            sections[section.id] = createWeekBreakdownMap();
            return;
        }
        if (section.valueType === "checklist") {
            sections[section.id] = [];
            return;
        }
        if (section.valueType === "weeklyListMap") {
            sections[section.id] = createMonthlyWeekMap();
            return;
        }
        sections[section.id] = "";
    });

    return {
        title,
        timePeriod,
        fromDate: null,
        toDate: null,
        focusItems: [],
        sections,
    };
};

const formatShortDay = (date) => date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });

const getWeekRangeText = (referenceDate, weekOffset = 0) => {
    const base = new Date(referenceDate);
    const dayOfWeek = base.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const start = new Date(base);
    start.setDate(base.getDate() + diffToMonday + (weekOffset * 7));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${formatShortDay(start)} - ${formatShortDay(end)}`;
};

const getMonthlyWeekRangeLabels = (referenceDate) => {
    const monthStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
    const monthStartDay = monthStart.getDay();
    const diffToMonday = monthStartDay === 0 ? -6 : 1 - monthStartDay;
    const firstWeekStart = new Date(monthStart);
    firstWeekStart.setDate(monthStart.getDate() + diffToMonday);

    return monthlyWeekKeys.reduce((acc, weekKey, index) => {
        const weekStart = new Date(firstWeekStart);
        weekStart.setDate(firstWeekStart.getDate() + (index * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        acc[weekKey] = `${formatShortDay(weekStart)} - ${formatShortDay(weekEnd)}`;
        return acc;
    }, {});
};

const getStartOfWeekDate = (referenceDate, weekOffset = 0) => {
    const base = new Date(referenceDate);
    const dayOfWeek = base.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const start = new Date(base);
    start.setDate(base.getDate() + diffToMonday + (weekOffset * 7));
    start.setHours(0, 0, 0, 0);
    return start;
};

const getStartOfMonthDate = (referenceDate, monthOffset = 0) => {
    const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + monthOffset, 1);
    date.setHours(0, 0, 0, 0);
    return date;
};

const formatTimeFromDate = (date) => `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

/** Maps frontend display type names to the PlannerWorkItemType backend enum values. */
const FRONTEND_TYPE_TO_BACKEND_ENUM = {
    Task: "TASK",
    Project: "PROJECT",
    Learning: "CUSTOM_ITEM",
    Note: "NOTE",
    "Recall Card": "RECALL_CARD_FOLDER",
    "Custom item": "CUSTOM_ITEM",
};

/** Maps backend PlannerWorkItemType enum values back to frontend display type names. */
const BACKEND_ENUM_TO_FRONTEND_TYPE = {
    TASK: "Task",
    PROJECT: "Project",
    NOTE: "Note",
    RECALL_CARD_FOLDER: "Recall Card",
    CUSTOM_ITEM: "Custom item",
};

const TIME_PREFIX_PATTERN = /^(\d{2}:\d{2}-\d{2}:\d{2})\s*\|\s*(.*)/;

/**
 * Encodes a frontend week-breakdown item into the PlannerWorkItem shape the
 * backend API expects: { type, item_id, custom_item }.
 *
 * Time range (if present) is preserved as a "HH:MM-HH:MM | title" prefix in
 * the custom_item field so it can be round-tripped without schema changes.
 */
const normalizeScheduleItem = (item) => {
    if (typeof item === "string") {
        // Legacy string format (shouldn't be sent to backend but kept for safety)
        return { type: "CUSTOM_ITEM", item_id: null, custom_item: item };
    }

    const title = String(item?.title || "").trim();
    const backendType = FRONTEND_TYPE_TO_BACKEND_ENUM[item?.type] ?? "CUSTOM_ITEM";

    const customItem = (item?.startTime && item?.endTime)
        ? `${item.startTime}-${item.endTime} | ${title}`
        : title || null;

    // item_id must be a UUID; refId may be a numeric string for tasks/projects –
    // only pass it through when it already looks like a UUID.
    const UUID_PATTERN = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;
    const itemId = (item?.refId && UUID_PATTERN.test(String(item.refId)))
        ? item.refId
        : null;

    return { type: backendType, item_id: itemId, custom_item: customItem };
};

/**
 * Converts a backend PlannerWorkItem object back into the frontend breakdown
 * item shape: { type, title, startTime, endTime }.
 *
 * Parses the optional "HH:MM-HH:MM | title" prefix from custom_item to restore
 * the time range that was stored when the item was saved.
 */
const denormalizeScheduleItem = (backendItem, index) => {
    if (typeof backendItem === "string") {
        return { type: "Custom item", title: backendItem, startTime: "", endTime: "", refId: null };
    }

    const frontendType = BACKEND_ENUM_TO_FRONTEND_TYPE[backendItem?.type] ?? "Custom item";
    const rawCustomItem = String(backendItem?.custom_item || "").trim();

    const timeMatch = TIME_PREFIX_PATTERN.exec(rawCustomItem);
    let title = rawCustomItem;
    let startTime = "";
    let endTime = "";

    if (timeMatch) {
        const [startPart, endPart] = timeMatch[1].split("-");
        startTime = startPart ?? "";
        endTime = endPart ?? "";
        title = timeMatch[2].trim();
    }

    return {
        type: frontendType,
        title,
        startTime,
        endTime,
        refId: backendItem?.item_id ?? null,
    };
};

const isWeekBreakdownTimeRangeValid = ({ title, startTime, endTime }) => {
    if (!title) return false;

    const hasStartTime = !!startTime;
    const hasEndTime = !!endTime;

    if (hasStartTime !== hasEndTime) return false;
    if (hasStartTime && hasEndTime && endTime <= startTime) return false;

    return true;
};

const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value == null) return [];
    return [value];
};

const buildMonthlyWeeklyMapFromFocus = (weeklyFocus) => {
    const nextMap = createMonthlyWeekMap();
    toArray(weeklyFocus).forEach((entry) => {
        const text = String(entry || "").trim();
        if (!text) return;
        const matchedWeek = monthlyWeekKeys.find((weekKey) => text.startsWith(`${weekKey}:`));
        if (!matchedWeek) return;
        const itemText = text.replace(`${matchedWeek}:`, "").trim();
        if (!itemText) return;
        nextMap[matchedWeek] = [...nextMap[matchedWeek], itemText];
    });
    return nextMap;
};

const parseFocusItemString = (itemText, index) => {
    // Handle PlannerWorkItem objects from backend
    if (itemText && typeof itemText === 'object') {
        const backendType = BACKEND_ENUM_TO_FRONTEND_TYPE[itemText.type] ?? "Custom item";
        const customItem = String(itemText.custom_item || "").trim();
        const timeMatch = TIME_PREFIX_PATTERN.exec(customItem);
        const startTime = timeMatch?.[1]?.split("-")?.[0] || "";
        const endTime = timeMatch?.[1]?.split("-")?.[1] || "";
        
        // Extract pomodoro count from format like "staff (2x)"
        const pomodoroMatch = customItem.match(/\((\d+)x\)$/);
        const pomodoros = pomodoroMatch ? pomodoroMatch[1] : "";
        let working = timeMatch ? timeMatch[2].trim() : customItem;
        if (pomodoroMatch) working = working.replace(/ \(\d+x\)$/, "");
        
        // Extract hours from format like "[3h]"
        const hoursMatch = working.match(/\[(\d+(?:\.\d+)?)h\]$/);
        const hours = hoursMatch ? hoursMatch[1] : "";
        if (hoursMatch) working = working.replace(/ \[\d+(?:\.\d+)?h\]$/, "");
        
        return {
            id: `focus-loaded-${index}`,
            type: backendType,
            title: working,
            itemRefId: itemText.item_id || null,
            plannedPomodoros: pomodoros,
            plannedHours: hours,
            startTime,
            endTime,
            targetValue: "",
            targetUnit: "pomodoros",
        };
    }
    
    // Handle legacy string format
    const raw = String(itemText || "").trim();
    const splitIndex = raw.indexOf(":");
    const maybeType = splitIndex > -1 ? raw.slice(0, splitIndex).trim() : "Custom item";
    const title = splitIndex > -1 ? raw.slice(splitIndex + 1).trim() : raw;

    return {
        id: `focus-loaded-${index}`,
        type: maybeType || "Custom item",
        title,
        plannedPomodoros: "",
        plannedHours: "",
        startTime: "",
        endTime: "",
        targetValue: "",
        targetUnit: "pomodoros",
    };
};

const mapBackendPlannerToPlan = (currentPlan, backendPlanner) => {
    const nextSections = {
        ...(currentPlan?.sections || {}),
        mainOutcome: backendPlanner?.main_outcome || "",
        reviewReflection: backendPlanner?.reflection || "",
        weeklyOutcomes: backendPlanner?.weekly_outcomes || "",
        weeklyReview: backendPlanner?.weekly_review || "",
        priorities: backendPlanner?.monthly_outcomes || "",
        weekBreakdown: {
            Mon: toArray(backendPlanner?.monday_work_items).map(denormalizeScheduleItem),
            Tue: toArray(backendPlanner?.tuesday_work_items).map(denormalizeScheduleItem),
            Wed: toArray(backendPlanner?.wednesday_work_items).map(denormalizeScheduleItem),
            Thu: toArray(backendPlanner?.thursday_work_items).map(denormalizeScheduleItem),
            Fri: toArray(backendPlanner?.friday_work_items).map(denormalizeScheduleItem),
            Sat: toArray(backendPlanner?.saturday_work_items).map(denormalizeScheduleItem),
            Sun: toArray(backendPlanner?.sunday_work_items).map(denormalizeScheduleItem),
        },
        milestones: toArray(backendPlanner?.milestones).map((item, idx) => ({
            id: `ms-loaded-${idx}`,
            label: String(item || ""),
            checked: false,
        })),
        projects: toArray(backendPlanner?.key_project).map((item, idx) => ({
            id: `proj-loaded-${idx}`,
            label: String(item || ""),
            refId: item,
        })),
        learning: toArray(backendPlanner?.learning_objective).map((item, idx) => ({
            id: `learn-loaded-${idx}`,
            type: "Custom",
            title: String(item || ""),
            refId: null,
        })),
        monthlyWeeklyBreakdown: buildMonthlyWeeklyMapFromFocus(backendPlanner?.weekly_focus),
    };

    const nextFocusItems = toArray(backendPlanner?.daily_work_items).map((item, idx) => parseFocusItemString(item, idx));

    return {
        ...currentPlan,
        backendPlannerId: backendPlanner?.id || currentPlan?.backendPlannerId,
        backendCreatedDate: backendPlanner?.created_date || currentPlan?.backendCreatedDate || null,
        sections: nextSections,
        focusItems: nextFocusItems,
    };
};

const loadPersistedPlannerBackendIds = () => {
    try {
        const raw = window.localStorage.getItem(PLANNER_BACKEND_IDS_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
        return {};
    }
};

const persistPlannerBackendId = (periodKey, plannerId) => {
    if (!periodKey || !plannerId) return;
    try {
        const current = loadPersistedPlannerBackendIds();
        window.localStorage.setItem(
            PLANNER_BACKEND_IDS_STORAGE_KEY,
            JSON.stringify({ ...current, [periodKey]: plannerId }),
        );
    } catch (error) {
        // Ignore localStorage write failures.
    }
};

const persistPlannerBackendIdsMap = (periodIdMap) => {
    try {
        const compact = Object.fromEntries(
            Object.entries(periodIdMap || {}).filter(([, value]) => !!value),
        );
        window.localStorage.setItem(PLANNER_BACKEND_IDS_STORAGE_KEY, JSON.stringify(compact));
    } catch (error) {
        // Ignore localStorage write failures.
    }
};

const resolveTemplatePeriodFromPlanner = (planner) => {
    const plannerType = planner?.planner_type;
    if (plannerType === "DAILY") return "today";
    if (plannerType === "WEEKLY") return "weekly";
    if (plannerType === "MONTHLY") return "monthly";
    return "custom";
};

const getPlannerTypeLabel = (plannerType) => {
    if (plannerType === "DAILY") return "Daily";
    if (plannerType === "WEEKLY") return "Weekly";
    if (plannerType === "MONTHLY") return "Monthly";
    return "Plan";
};

const getPlannerSortTimestamp = (planner, dateKey = null) => {
    if (typeof planner?.plan_date === "number") return planner.plan_date;
    if (typeof planner?.daily_date === "number") return planner.daily_date;
    if (typeof planner?.week_date === "number") return planner.week_date;
    if (typeof planner?.month_date === "number") return planner.month_date;

    if (dateKey) {
        const parsed = new Date(dateKey);
        if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
    }

    return Number(planner?.updated_date || planner?.created_date || 0);
};

const getPlannerDateText = (planner, fallbackDateKey = null) => {
    const timestamp = getPlannerSortTimestamp(planner);
    if (!timestamp) return fallbackDateKey;

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return fallbackDateKey;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

function Planner() {
    const now = React.useMemo(() => new Date(), []);
    const todayDisplay = now.toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" });
    const { authToken } = useAuth();
    const navigate = useNavigate();
    const { data: projectsData } = useProjects(authToken);
    const { data: tasksData } = useTask(authToken);
    const { data: notesData } = useNotes(authToken);
    const { data: recallCardsData } = useRecallCards(authToken);
    const { data: foldersData } = useFolders(authToken);
    const { mutateAsync: upsertPlannerPlan, isLoading: isSavingPlanner } = useUpsertPlanner(authToken);
    const { mutateAsync: updateTaskItem } = useUpdateTask(authToken);
    const { data: plannerOverviewData, isFetched: isPlannerOverviewFetched } = usePlannerOverviews(authToken);
    const { data: dailyStatsData } = useStats(authToken, "DAILY");

    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const periodMetaByKey = {
        today: formatShortDay(now),
        tomorrow: formatShortDay(tomorrowDate),
        weekly: getWeekRangeText(now, 0),
        nextweek: getWeekRangeText(now, 1),
        monthly: now.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
        nextmonth: new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    };

    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [isMobileOpen, setIsMobileOpen] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
    const [isAddPlanModalOpen, setIsAddPlanModalOpen] = React.useState(false);
    const [plansByPeriod, setPlansByPeriod] = React.useState(() => {
        const persistedIds = loadPersistedPlannerBackendIds();
        return {
            today: { ...buildEmptyPlan("today", "Today Plan"), backendPlannerId: persistedIds.today || null },
            tomorrow: { ...buildEmptyPlan("tomorrow", "Tomorrow Plan"), backendPlannerId: persistedIds.tomorrow || null },
            weekly: { ...buildEmptyPlan("weekly", "This Week Plan"), backendPlannerId: persistedIds.weekly || null },
            nextweek: { ...buildEmptyPlan("nextweek", "Next Week Plan"), backendPlannerId: persistedIds.nextweek || null },
            monthly: { ...buildEmptyPlan("monthly", "This Month Plan"), backendPlannerId: persistedIds.monthly || null },
            nextmonth: { ...buildEmptyPlan("nextmonth", "Next Month Plan"), backendPlannerId: persistedIds.nextmonth || null },
        };
    });
    const [customPeriods, setCustomPeriods] = React.useState([]);
    const [archivedPlans, setArchivedPlans] = React.useState([]);
    const [isUpcomingExpanded, setIsUpcomingExpanded] = React.useState(false);
    const [isArchivedPlansExpanded, setIsArchivedPlansExpanded] = React.useState(false);
    const [selectedPlanRef, setSelectedPlanRef] = React.useState({ type: "static", key: "today" });
    const [modalContext, setModalContext] = React.useState({
        mode: null,
        period: "today",
        customId: null,
    });
    const [expandedFocusMenus, setExpandedFocusMenus] = React.useState({});
    const [expandedFocusOptions, setExpandedFocusOptions] = React.useState({});
    const [isViewMode, setIsViewMode] = React.useState(true);
    const [contentView, setContentView] = React.useState("details");
    const [taskDeadlineOverrides, setTaskDeadlineOverrides] = React.useState({});
    const [calendarEntryModalState, setCalendarEntryModalState] = React.useState(null);
    const [weekBreakdownFormState, setWeekBreakdownFormState] = React.useState(() => createWeekBreakdownInputState());
    const [checklistInputs, setChecklistInputs] = React.useState({});
    const [learningObjectiveFormState, setLearningObjectiveFormState] = React.useState({
        type: "Note",
        title: "",
        refId: null,
    });
    const [monthlyWeeklyInputs, setMonthlyWeeklyInputs] = React.useState(() => createMonthlyWeekMap());

    // Handle different response structures - API might return array directly or wrapped in data property
    const projectsList = React.useMemo(() => (
        Array.isArray(projectsData) ? projectsData : (projectsData?.data || [])
    ), [projectsData]);
    const tasksList = React.useMemo(() => (
        Array.isArray(tasksData) ? tasksData : (tasksData?.data || [])
    ), [tasksData]);
    const notesList = React.useMemo(() => (
        Array.isArray(notesData) ? notesData : (notesData?.data || [])
    ), [notesData]);
    const recallCardsList = React.useMemo(() => (
        Array.isArray(recallCardsData) ? recallCardsData : (recallCardsData?.data || [])
    ), [recallCardsData]);
    const foldersList = React.useMemo(() => (
        Array.isArray(foldersData) ? foldersData : (foldersData?.data || [])
    ), [foldersData]);
    const calendarTasksList = React.useMemo(
        () => tasksList.map((task) => (
            taskDeadlineOverrides[task.id]
                ? { ...task, deadlineDate: taskDeadlineOverrides[task.id] }
                : task
        )),
        [taskDeadlineOverrides, tasksList],
    );

    // Reset view mode when switching plans
    React.useEffect(() => {
        setIsViewMode(true);
        setWeekBreakdownFormState(createWeekBreakdownInputState());
        setChecklistInputs({});
        setLearningObjectiveFormState({ type: "Note", title: "", refId: null });
        setMonthlyWeeklyInputs(createMonthlyWeekMap());
    }, [selectedPlanRef.key, selectedPlanRef.type]);

    React.useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setIsCollapsed(true);
                setIsMobileOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const selectedStaticPeriod = selectedPlanRef.type === "static" ? selectedPlanRef.key : "custom";
    const selectedCustomPeriod = selectedPlanRef.type === "custom"
        ? customPeriods.find((period) => period.id === selectedPlanRef.key)
        : null;
    const activePlan = selectedPlanRef.type === "static"
        ? plansByPeriod[selectedPlanRef.key]
        : selectedCustomPeriod?.plan || null;
    const isArchivedView = selectedPlanRef.type === "archived";
    const activePlannerId = isArchivedView ? selectedPlanRef.key : null;
    const { data: plannerByIdData, isLoading: isLoadingArchivedPlan } = usePlannerById(authToken, activePlannerId);

    // For archived view we build an ephemeral plan directly from the fetched data
    const archivedPlanData = React.useMemo(() => {
        if (!isArchivedView || !plannerByIdData?.id) return null;
        return mapBackendPlannerToPlan(buildEmptyPlan("custom", "Archived Plan"), plannerByIdData);
    }, [isArchivedView, plannerByIdData]);

    const archivedPlanEntry = archivedPlans.find((a) => a.id === selectedPlanRef.key) || null;
    const archivedPeriodKey = archivedPlanEntry?.periodKey || null;
    const archivedTemplateKey = resolveTemplateKey(archivedPeriodKey || "custom");

    const effectivePeriod = isArchivedView
        ? (archivedPeriodKey || "custom")
        : (activePlan?.timePeriod || selectedStaticPeriod);
    const monthlyReferenceDate = effectivePeriod === "nextmonth"
        ? new Date(now.getFullYear(), now.getMonth() + 1, 1)
        : now;
    const monthlyWeekRangeLabels = getMonthlyWeekRangeLabels(monthlyReferenceDate);
    const templateKey = isArchivedView ? archivedTemplateKey : resolveTemplateKey(effectivePeriod);
    const planSections = sectionConfigs[templateKey] || sectionConfigs.custom;
    const planTitle = isArchivedView
        ? (archivedPlanEntry ? `${archivedPlanEntry.title}${archivedPlanEntry.subtitle ? ` — ${archivedPlanEntry.subtitle}` : ""}` : "Archived Plan")
        : (activePlan?.title || `${timePeriodLabels[effectivePeriod] || "Custom"} Plan`);
    const planFocusItems = isArchivedView
        ? (archivedPlanData?.focusItems || [])
        : (activePlan?.focusItems || []);
    const planSectionsData = isArchivedView
        ? (archivedPlanData?.sections || buildEmptyPlan("custom").sections)
        : (activePlan?.sections || buildEmptyPlan(effectivePeriod).sections);
    const isDayPlan = templateKey === "today" || templateKey === "tomorrow";
    const isWeeklyPlan = templateKey === "weekly";
    const isMonthlyPlan = templateKey === "monthly";
    const sectionsToRender = isDayPlan
        ? planSections.filter((section) => section.id !== "mainOutcome")
        : isWeeklyPlan
            ? planSections.filter((section) => section.id !== "weeklyOutcomes")
            : isMonthlyPlan
                ? planSections.filter((section) => section.id !== "priorities")
                : planSections;

    const planDateDisplay = (() => {
        if (isArchivedView) {
            return archivedPlanEntry?.dateText || "Archived";
        }
        if (effectivePeriod === "today") {
            return todayDisplay;
        }
        if (effectivePeriod === "tomorrow") {
            const tomorrow = new Date(now);
            tomorrow.setDate(now.getDate() + 1);
            return tomorrow.toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" });
        }
        if (effectivePeriod === "weekly" || effectivePeriod === "nextweek") {
            return periodMetaByKey[effectivePeriod] || "";
        }
        if (effectivePeriod === "monthly" || effectivePeriod === "nextmonth") {
            return periodMetaByKey[effectivePeriod] || "";
        }
        if (effectivePeriod === "custom") {
            if (activePlan?.fromDate && activePlan?.toDate) {
                return `${activePlan.fromDate} - ${activePlan.toDate}`;
            }
            if (activePlan?.fromDate) return activePlan.fromDate;
            if (activePlan?.toDate) return activePlan.toDate;
        }
        return todayDisplay;
    })();
    const calendarPlanDate = (() => {
        if (isArchivedView) {
            return plannerByIdData?.plan_date
                ?? plannerByIdData?.daily_date
                ?? plannerByIdData?.week_date
                ?? plannerByIdData?.month_date
                ?? null;
        }

        if (effectivePeriod === "today") {
            const date = new Date(now);
            date.setHours(0, 0, 0, 0);
            return date;
        }

        if (effectivePeriod === "tomorrow") {
            const date = new Date(now);
            date.setDate(date.getDate() + 1);
            date.setHours(0, 0, 0, 0);
            return date;
        }

        if (effectivePeriod === "weekly") return getStartOfWeekDate(now, 0);
        if (effectivePeriod === "nextweek") return getStartOfWeekDate(now, 1);
        if (effectivePeriod === "monthly") return getStartOfMonthDate(now);
        if (effectivePeriod === "nextmonth") return getStartOfMonthDate(now, 1);
        return activePlan?.fromDate || null;
    })();
    const calendarSupportingWeekPeriodKey = React.useMemo(() => {
        if (isArchivedView || (effectivePeriod !== "today" && effectivePeriod !== "tomorrow")) return null;

        const targetDate = calendarPlanDate ? new Date(calendarPlanDate) : new Date(now);
        const targetWeekStartTs = getStartOfWeekDate(targetDate, 0).getTime();
        const nextWeekStartTs = getStartOfWeekDate(now, 1).getTime();

        return targetWeekStartTs === nextWeekStartTs ? "nextweek" : "weekly";
    }, [calendarPlanDate, effectivePeriod, isArchivedView, now]);
    const calendarSupportingWeekBreakdown = React.useMemo(() => (
        calendarSupportingWeekPeriodKey
            ? plansByPeriod?.[calendarSupportingWeekPeriodKey]?.sections?.weekBreakdown || null
            : null
    ), [calendarSupportingWeekPeriodKey, plansByPeriod]);

    const totalPomodoros = planFocusItems.reduce((sum, item) => sum + (Number(item.plannedPomodoros) || 0), 0);
    const completedPomodoros = effectivePeriod === "today" ? (dailyStatsData?.completedPomodoros || 0) : null;
    const totalHoursRaw = planFocusItems.reduce((sum, item) => sum + (Number(item.plannedHours) || 0), 0);
    const totalHours = Number.isInteger(totalHoursRaw) ? totalHoursRaw : Number(totalHoursRaw.toFixed(2));

    const applyStaticPlanUpdate = (periodKey, updater) => {
        if (!periodKey) return;

        setPlansByPeriod((prev) => {
            const currentPlan = prev?.[periodKey] || buildEmptyPlan(periodKey, `${timePeriodLabels[periodKey] || "Custom"} Plan`);
            const nextPlan = typeof updater === "function" ? updater(currentPlan) : updater;

            return {
                ...prev,
                [periodKey]: { ...nextPlan, timePeriod: periodKey },
            };
        });
    };

    const resolveWeekPeriodKeyForDate = React.useCallback((date) => {
        const clickedWeekStartTs = getStartOfWeekDate(date, 0).getTime();
        const currentWeekStartTs = getStartOfWeekDate(now, 0).getTime();
        const nextWeekStartTs = getStartOfWeekDate(now, 1).getTime();

        if (clickedWeekStartTs === currentWeekStartTs) return "weekly";
        if (clickedWeekStartTs === nextWeekStartTs) return "nextweek";
        return null;
    }, [now]);

    const applyPlanUpdate = (updater) => {
        const currentPlan = activePlan || buildEmptyPlan(effectivePeriod, `${timePeriodLabels[effectivePeriod] || "Custom"} Plan`);
        const nextPlan = typeof updater === "function" ? updater(currentPlan) : updater;

        if (selectedPlanRef.type === "static") {
            setPlansByPeriod((prev) => ({
                ...prev,
                [selectedPlanRef.key]: { ...nextPlan, timePeriod: selectedPlanRef.key },
            }));
            return;
        }

        setCustomPeriods((prev) => prev.map((period) => (
            period.id === selectedPlanRef.key
                ? {
                    ...period,
                    name: nextPlan.title || period.name,
                    plan: { ...nextPlan, timePeriod: "custom" },
                }
                : period
        )));
    };

    const addCalendarScheduleItem = ({ targetPeriodKey, dayKey, item }) => {
        if (!targetPeriodKey || !dayKey || !item?.title) return;

        setIsViewMode(false);
        applyStaticPlanUpdate(targetPeriodKey, (plan) => {
            const currentBreakdown = plan.sections?.weekBreakdown || createWeekBreakdownMap();
            const currentItems = Array.isArray(currentBreakdown[dayKey]) ? currentBreakdown[dayKey] : [];

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    weekBreakdown: {
                        ...currentBreakdown,
                        [dayKey]: [...currentItems, item],
                    },
                },
            };
        });
    };

    React.useEffect(() => {
        if (!isPlannerOverviewFetched) return;

        const latestIdByPeriod = {};
        const activePeriodIds = new Set();
        const archivedById = new Map();

        staticPeriods.forEach((periodKey) => {
            const overview = plannerOverviewData?.[periodKey];
            const activePlanner = overview?.today || null;

            if (activePlanner?.id) {
                latestIdByPeriod[periodKey] = activePlanner.id;
                activePeriodIds.add(activePlanner.id);
            }

            Object.entries(overview?.plans_by_date || {}).forEach(([dateKey, planner]) => {
                if (!planner?.id || archivedById.has(planner.id)) return;

                // The backend date key can be one day earlier when its timezone differs
                // from the browser that created the local-midnight plan timestamp.
                const localDateText = getPlannerDateText(planner, dateKey);
                archivedById.set(planner.id, {
                    id: planner.id,
                    title: localDateText || planner.id.slice(0, 8),
                    subtitle: getPlannerTypeLabel(planner?.planner_type),
                    periodKey: resolveTemplatePeriodFromPlanner(planner),
                    dateText: localDateText || null,
                    sortTs: getPlannerSortTimestamp(planner, dateKey),
                });
            });
        });

        const nextArchived = Array.from(archivedById.values())
            .filter((planner) => !activePeriodIds.has(planner.id))
            .sort((a, b) => b.sortTs - a.sortTs)
            .map(({ sortTs, ...planner }) => planner);

        setArchivedPlans(nextArchived);

        setPlansByPeriod((prev) => {
            const next = { ...prev };
            staticPeriods.forEach((periodKey) => {
                if (!next[periodKey]) return;
                const nextTitle = next[periodKey]?.title || `${timePeriodLabels[periodKey] || "Custom"} Plan`;
                const activePlanner = plannerOverviewData?.[periodKey]?.today || null;
                const emptyPlan = buildEmptyPlan(periodKey, nextTitle);

                next[periodKey] = activePlanner?.id
                    ? {
                        ...mapBackendPlannerToPlan(emptyPlan, activePlanner),
                        timePeriod: periodKey,
                    }
                    : {
                        ...emptyPlan,
                        backendPlannerId: null,
                    };
            });
            persistPlannerBackendIdsMap(latestIdByPeriod);
            return next;
        });
    }, [isPlannerOverviewFetched, plannerOverviewData]);

    const buildPlannerPayload = (plan) => {
        const sections = plan?.sections || {};
        const weekBreakdown = sections.weekBreakdown || createWeekBreakdownMap();
        const monthlyBreakdown = sections.monthlyWeeklyBreakdown || createMonthlyWeekMap();
        const milestones = Array.isArray(sections.milestones)
            ? sections.milestones.map((item) => (typeof item === "string" ? item : item.label)).filter(Boolean)
            : [];

        const keyProjects = Array.isArray(sections.projects)
            ? sections.projects.map((item) => item.refId || item.id || item.label).filter(Boolean)
            : [];

        const learningObjectives = Array.isArray(sections.learning)
            ? sections.learning.map((item) => item.refId || item.title || item.label).filter(Boolean)
            : [];

        // Normalize daily work items directly from plan.focusItems objects
        const dailyWorkItems = Array.isArray(plan?.focusItems)
            ? plan.focusItems.map((item) => {
                const title = String(item?.title || "").trim();
                const pomodoros = String(item?.plannedPomodoros || "").trim();
                const hours = String(item?.plannedHours || "").trim();
                
                let customPart = title;
                if (pomodoros) customPart += ` (${pomodoros}x)`;
                if (hours) customPart += ` [${hours}h]`;
                if (item?.startTime && item?.endTime) {
                    customPart = `${item.startTime}-${item.endTime} | ${customPart}`;
                }
                
                const backendType = FRONTEND_TYPE_TO_BACKEND_ENUM[item?.type] ?? "CUSTOM_ITEM";
                
                // Check if itemRefId is a UUID; only pass it through when it's a valid UUID
                const UUID_PATTERN = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;
                const itemId = (item?.itemRefId && UUID_PATTERN.test(String(item.itemRefId)))
                    ? item.itemRefId
                    : null;
                
                return { type: backendType, item_id: itemId, custom_item: customPart };
            })
            : [];

        // planner_type & plan_date — single date for the upsert key
        const plannerType = (() => {
            if (effectivePeriod === "today" || effectivePeriod === "tomorrow") return "DAILY";
            if (effectivePeriod === "weekly" || effectivePeriod === "nextweek") return "WEEKLY";
            if (effectivePeriod === "monthly" || effectivePeriod === "nextmonth") return "MONTHLY";
            return "CUSTOM";
        })();

        const planDate = (() => {
            if (effectivePeriod === "today") {
                const d = new Date(now); d.setHours(0, 0, 0, 0); return d.getTime();
            }
            if (effectivePeriod === "tomorrow") {
                const d = new Date(now); d.setDate(now.getDate() + 1); d.setHours(0, 0, 0, 0); return d.getTime();
            }
            if (effectivePeriod === "weekly") return getStartOfWeekDate(now, 0).getTime();
            if (effectivePeriod === "nextweek") return getStartOfWeekDate(now, 1).getTime();
            if (effectivePeriod === "monthly") {
                return getStartOfMonthDate(now).getTime();
            }
            if (effectivePeriod === "nextmonth") return getStartOfMonthDate(now, 1).getTime();
            return now.getTime();
        })();

        const nowTs = Date.now();

        const weeklyFocus = monthlyWeekKeys.flatMap((weekKey) => {
            const items = Array.isArray(monthlyBreakdown[weekKey]) ? monthlyBreakdown[weekKey] : [];
            return items.map((item) => `${weekKey}: ${item}`);
        });

        return {
            planner_type: plannerType,
            plan_date: planDate,
            main_outcome: sections.mainOutcome || null,
            daily_date: plannerType === "DAILY" ? planDate : null,
            daily_work_items: dailyWorkItems,
            reflection: sections.reviewReflection || null,
            weekly_outcomes: sections.weeklyOutcomes || null,
            week_date: plannerType === "WEEKLY" ? planDate : null,
            monday_work_items: (weekBreakdown.Mon || []).map(normalizeScheduleItem),
            tuesday_work_items: (weekBreakdown.Tue || []).map(normalizeScheduleItem),
            wednesday_work_items: (weekBreakdown.Wed || []).map(normalizeScheduleItem),
            thursday_work_items: (weekBreakdown.Thu || []).map(normalizeScheduleItem),
            friday_work_items: (weekBreakdown.Fri || []).map(normalizeScheduleItem),
            saturday_work_items: (weekBreakdown.Sat || []).map(normalizeScheduleItem),
            sunday_work_items: (weekBreakdown.Sun || []).map(normalizeScheduleItem),
            weekly_review: sections.weeklyReview || null,
            monthly_outcomes: sections.priorities || null,
            key_project: keyProjects.length ? keyProjects : null,
            learning_objective: learningObjectives.length ? learningObjectives : null,
            weekly_focus: weeklyFocus,
            milestones,
            month_date: plannerType === "MONTHLY" ? planDate : null,
            created_date: plan?.backendCreatedDate || nowTs,
            updated_date: nowTs,
            end_date: null,
        };
    };

    const handleSavePlan = async () => {
        if (!activePlan || !authToken) {
            setIsViewMode(true);
            return;
        }

        try {
            const payload = buildPlannerPayload(activePlan);
            const savedPlanner = await upsertPlannerPlan(payload);
            if (savedPlanner?.id) {
                applyPlanUpdate((plan) => ({
                    ...plan,
                    backendPlannerId: savedPlanner.id,
                    backendCreatedDate: savedPlanner.created_date || plan?.backendCreatedDate || null,
                }));
                if (selectedPlanRef.type === "static") {
                    persistPlannerBackendId(selectedPlanRef.key, savedPlanner.id);
                }
            }
            setIsViewMode(true);
        } catch (error) {
            console.error("Failed to save planner", error);
        }
    };

    const addInlineFocusItem = () => {
        applyPlanUpdate((plan) => ({
            ...plan,
            focusItems: [
                ...(plan.focusItems || []),
                {
                    id: `focus-${Date.now()}`,
                    type: "Task",
                    title: "",
                    plannedPomodoros: "",
                    plannedHours: "",
                    startTime: "",
                    endTime: "",
                    targetValue: "",
                    targetUnit: "pomodoros",
                },
            ],
        }));
    };

    const removeInlineFocusItem = (itemId) => {
        applyPlanUpdate((plan) => ({
            ...plan,
            focusItems: (plan.focusItems || []).filter((item) => item.id !== itemId),
        }));
    };

    const updateInlineFocusItem = (itemId, updates) => {
        applyPlanUpdate((plan) => ({
            ...plan,
            focusItems: (plan.focusItems || []).map((item) => (
                item.id === itemId ? { ...item, ...updates } : item
            )),
        }));
    };

    const toggleInlineFocusMenu = (itemId) => {
        setExpandedFocusMenus((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    const selectInlineFocusType = (itemId, type) => {
        updateInlineFocusItem(itemId, { type });
        setExpandedFocusMenus((prev) => ({ ...prev, [itemId]: false }));
    };

    const selectItemFromOptions = (itemId, itemName, refId) => {
        updateInlineFocusItem(itemId, { title: itemName, itemRefId: refId ?? null });
        setExpandedFocusOptions((prev) => ({ ...prev, [itemId]: false }));
    };

    const updateInlineSection = (sectionId, value) => {
        applyPlanUpdate((plan) => ({
            ...plan,
            sections: {
                ...plan.sections,
                [sectionId]: value,
            },
        }));
    };

    const updateDayAllocationEntry = (sectionId, dayKey, value) => {
        applyPlanUpdate((plan) => ({
            ...plan,
            sections: {
                ...plan.sections,
                [sectionId]: {
                    ...(plan.sections?.[sectionId] || createWeekdayMap()),
                    [dayKey]: value,
                },
            },
        }));
    };

    const getItemsForBreakdownType = (type) => {
        if (type === "Task") return tasksList;
        if (type === "Project") return projectsList;
        return [];
    };

    const getLearningItemsForType = (type) => {
        if (type === "Note") {
            return notesList.map((note) => ({
                id: note.id,
                label: note.title || note.plainText || `Note #${note.id}`,
            }));
        }
        if (type === "Recall Card") {
            return recallCardsList.map((card) => ({
                id: card.id,
                label: card.front || card.back || `Recall card #${card.id}`,
            }));
        }
        if (type === "Folder") {
            return foldersList.map((folder) => ({
                id: folder.id,
                label: folder.name || `Folder #${folder.id}`,
            }));
        }
        return [];
    };

    const toggleMonthlyProject = (project) => {
        const projectId = String(project.id);
        const projectLabel = project.name || project.title || `Project #${project.id}`;

        applyPlanUpdate((plan) => {
            const currentItems = Array.isArray(plan.sections?.projects) ? plan.sections.projects : [];
            const exists = currentItems.some((item) => String(item.id) === projectId);
            const nextItems = exists
                ? currentItems.filter((item) => String(item.id) !== projectId)
                : [...currentItems, { id: projectId, label: projectLabel, refId: project.id }];

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    projects: nextItems,
                },
            };
        });
    };

    const addChecklistItem = (sectionId) => {
        const rawValue = checklistInputs[sectionId] || "";
        const label = String(rawValue).trim();
        if (!label) return;

        applyPlanUpdate((plan) => {
            const currentItems = Array.isArray(plan.sections?.[sectionId]) ? plan.sections[sectionId] : [];
            const nextItem = {
                id: `check-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                label,
                checked: false,
            };

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    [sectionId]: [...currentItems, nextItem],
                },
            };
        });

        setChecklistInputs((prev) => ({ ...prev, [sectionId]: "" }));
    };

    const toggleChecklistItem = (sectionId, itemId) => {
        applyPlanUpdate((plan) => {
            const currentItems = Array.isArray(plan.sections?.[sectionId]) ? plan.sections[sectionId] : [];
            const nextItems = currentItems.map((item) => {
                if (String(item.id) !== String(itemId)) return item;
                return { ...item, checked: !item.checked };
            });

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    [sectionId]: nextItems,
                },
            };
        });
    };

    const removeChecklistItem = (sectionId, itemId) => {
        applyPlanUpdate((plan) => {
            const currentItems = Array.isArray(plan.sections?.[sectionId]) ? plan.sections[sectionId] : [];

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    [sectionId]: currentItems.filter((item) => String(item.id) !== String(itemId)),
                },
            };
        });
    };

    const addLearningObjectiveItem = (sectionId) => {
        const title = String(learningObjectiveFormState.title || "").trim();
        if (!title) return;

        applyPlanUpdate((plan) => {
            const currentItems = Array.isArray(plan.sections?.[sectionId]) ? plan.sections[sectionId] : [];
            const nextItem = {
                id: `learn-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                type: learningObjectiveFormState.type,
                title,
                refId: learningObjectiveFormState.refId ?? null,
            };

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    [sectionId]: [...currentItems, nextItem],
                },
            };
        });

        setLearningObjectiveFormState((prev) => ({ ...prev, title: "", refId: null }));
    };

    const removeLearningObjectiveItem = (sectionId, itemId) => {
        applyPlanUpdate((plan) => {
            const currentItems = Array.isArray(plan.sections?.[sectionId]) ? plan.sections[sectionId] : [];

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    [sectionId]: currentItems.filter((item) => String(item.id) !== String(itemId)),
                },
            };
        });
    };

    const addMonthlyWeekItem = (sectionId, weekKey) => {
        const rawValue = monthlyWeeklyInputs[weekKey] || "";
        const label = String(rawValue).trim();
        if (!label) return;

        applyPlanUpdate((plan) => {
            const currentMap = plan.sections?.[sectionId] || createMonthlyWeekMap();
            const currentItems = Array.isArray(currentMap[weekKey]) ? currentMap[weekKey] : [];

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    [sectionId]: {
                        ...currentMap,
                        [weekKey]: [...currentItems, label],
                    },
                },
            };
        });

        setMonthlyWeeklyInputs((prev) => ({ ...prev, [weekKey]: "" }));
    };

    const removeMonthlyWeekItem = (sectionId, weekKey, indexToRemove) => {
        applyPlanUpdate((plan) => {
            const currentMap = plan.sections?.[sectionId] || createMonthlyWeekMap();
            const currentItems = Array.isArray(currentMap[weekKey]) ? currentMap[weekKey] : [];

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    [sectionId]: {
                        ...currentMap,
                        [weekKey]: currentItems.filter((_, idx) => idx !== indexToRemove),
                    },
                },
            };
        });
    };

     const addWeekBreakdownScheduleItem = (dayKey) => {
          const formState = weekBreakdownFormState[dayKey];
          const { type, title, startTime, endTime } = formState;

          if (!isWeekBreakdownTimeRangeValid(formState)) return;

         applyPlanUpdate((plan) => {
             const currentBreakdown = plan.sections?.weekBreakdown || createWeekBreakdownMap();
             const currentItems = Array.isArray(currentBreakdown[dayKey]) ? currentBreakdown[dayKey] : [];

             return {
                 ...plan,
                 sections: {
                     ...plan.sections,
                     weekBreakdown: {
                         ...currentBreakdown,
                         [dayKey]: [...currentItems, { type, title, startTime, endTime, refId: null }],
                     },
                 },
             };
         });

         setWeekBreakdownFormState((prev) => ({
             ...prev,
             [dayKey]: { type: "Task", title: "", startTime: "", endTime: "" },
         }));
     };

    const removeWeekBreakdownScheduleItem = (dayKey, indexToRemove) => {
        applyPlanUpdate((plan) => {
            const currentBreakdown = plan.sections?.weekBreakdown || createWeekBreakdownMap();
            const currentItems = Array.isArray(currentBreakdown[dayKey]) ? currentBreakdown[dayKey] : [];

            return {
                ...plan,
                sections: {
                    ...plan.sections,
                    weekBreakdown: {
                        ...currentBreakdown,
                        [dayKey]: currentItems.filter((_, index) => index !== indexToRemove),
                    },
                },
            };
        });
    };

    const openCreateCustomPeriodModal = () => {
        setModalContext({
            mode: "create-custom",
            period: "today",
            customId: null,
        });
        setIsAddPlanModalOpen(true);
    };

    const startSessionForItem = (item) => {
        // Use stored ref ID if available; otherwise fall back to name-based lookup
        let taskId = null;
        let projectId = null;

        if (item.itemRefId) {
            if (item.type === "Task") taskId = String(item.itemRefId);
            else if (item.type === "Project") projectId = String(item.itemRefId);
        } else {
            // Fallback: match by display name
            const matchedTask = tasksList.find((t) => (t.text || t.title || t.name) === item.title);
            const matchedProject = projectsList.find((p) => (p.name || p.title) === item.title);
            if (matchedTask) taskId = String(matchedTask.id);
            if (matchedProject) projectId = String(matchedProject.id);
        }

        navigate("/session", { state: { taskId, projectId } });
    };

    const handleCalendarPlannerScheduleMove = async (changeInfo) => {
        const sourcePlanPeriod = changeInfo.event.extendedProps?.sourcePlanPeriod;
        const sourceDayKey = changeInfo.event.extendedProps?.sourceDayKey;
        const sourceIndex = Number(changeInfo.event.extendedProps?.itemIndex);

        if (!sourceDayKey || Number.isNaN(sourceIndex) || !changeInfo.event.start) {
            changeInfo.revert();
            return;
        }

        setIsViewMode(false);
        const applyScheduleMove = (plan) => ({
            ...plan,
            sections: {
                ...plan.sections,
                weekBreakdown: updateWeekBreakdownAfterDrop(plan.sections?.weekBreakdown, {
                    sourceDayKey,
                    sourceIndex,
                    start: changeInfo.event.start,
                    end: changeInfo.event.end,
                    allDay: changeInfo.event.allDay,
                }),
            },
        });

        if (sourcePlanPeriod && staticPeriods.includes(sourcePlanPeriod)) {
            applyStaticPlanUpdate(sourcePlanPeriod, applyScheduleMove);
            return;
        }

        applyPlanUpdate(applyScheduleMove);
    };

    const handleCalendarDateDoubleClick = (clickInfo) => {
        if (isArchivedView || !clickInfo?.date) return;

        const clickedDate = new Date(clickInfo.date);
        const targetPeriodKey = resolveWeekPeriodKeyForDate(clickedDate);

        if (!targetPeriodKey) return;

        const dayKey = weekdayKeyByDayNumber[clickedDate.getDay()];
        if (!dayKey) return;

        const initialStartTime = clickInfo.allDay ? "" : formatTimeFromDate(clickedDate);
        const initialEndTime = clickInfo.allDay
            ? ""
            : formatTimeFromDate(new Date(clickedDate.getTime() + (30 * 60 * 1000)));

        setCalendarEntryModalState({
            targetPeriodKey,
            dayKey,
            dateLabel: clickedDate.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
            }),
            periodLabel: timePeriodLabels[targetPeriodKey] || targetPeriodKey,
            initialStartTime,
            initialEndTime,
        });
    };

    const handleCalendarTodayClick = React.useCallback(() => {
        if (selectedPlanRef.type !== "static") return false;
        if (selectedPlanRef.key === "today") return false;

        setSelectedPlanRef({ type: "static", key: "today" });
        setContentView("calendar");
        return true;
    }, [selectedPlanRef.key, selectedPlanRef.type]);

    const handleCalendarEntrySubmit = (item) => {
        if (!calendarEntryModalState) return;

        addCalendarScheduleItem({
            targetPeriodKey: calendarEntryModalState.targetPeriodKey,
            dayKey: calendarEntryModalState.dayKey,
            item,
        });
        setCalendarEntryModalState(null);
    };

    const handleCalendarTaskDeadlineMove = async (changeInfo) => {
        const taskId = changeInfo.event.extendedProps?.taskId;
        const taskToUpdate = tasksList.find((task) => String(task?.id) === String(taskId));

        if (!taskId || !taskToUpdate || !changeInfo.event.start) {
            changeInfo.revert();
            return;
        }

        const nextDeadline = new Date(changeInfo.event.start);
        nextDeadline.setHours(0, 0, 0, 0);

        setTaskDeadlineOverrides((prev) => ({ ...prev, [taskId]: nextDeadline.getTime() }));

        try {
            await updateTaskItem({
                id: taskId,
                task: {
                    title: taskToUpdate.text || taskToUpdate.title || taskToUpdate.name || "",
                    deadline: nextDeadline,
                    repeat: taskToUpdate.repeat ?? false,
                    priority: taskToUpdate.priority ?? taskToUpdate.taskPriority ?? "",
                    projectId: taskToUpdate.projectId ?? null,
                },
            });
        } catch (error) {
            setTaskDeadlineOverrides((prev) => {
                const next = { ...prev };
                delete next[taskId];
                return next;
            });
            throw error;
        }
    };

    const desktopWidthClass = isCollapsed ? "w-[76px]" : "w-[240px]";
    const showDrawer = isMobile ? isMobileOpen : true;
    let sidebarToggleAriaLabel = "Close sidebar";
    let sidebarToggleIcon = <X size={16} />;

    if (!isMobile) {
        if (isCollapsed) {
            sidebarToggleAriaLabel = "Expand sidebar";
            sidebarToggleIcon = <ChevronRight size={16} />;
        } else {
            sidebarToggleAriaLabel = "Collapse sidebar";
            sidebarToggleIcon = <ChevronLeft size={16} />;
        }
    }

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header/>
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                {isMobile && !isMobileOpen && (
                    <button
                        type="button"
                        onClick={() => setIsMobileOpen(true)}
                        className="fixed left-4 top-[76px] z-40 rounded-xl border border-[var(--border-color)] bg-[var(--header-bg)] p-2 text-[var(--text-primary)] shadow-lg"
                        aria-label="Open planner sidebar"
                    >
                        <Menu size={20} />
                    </button>
                )}

                {isMobile && isMobileOpen && (
                    <button
                        type="button"
                        onClick={() => setIsMobileOpen(false)}
                        className="fixed inset-0 z-30 bg-black/50"
                        aria-label="Close planner sidebar overlay"
                    />
                )}

                <aside
                    className={[
                        "z-40 shrink-0 transition-all duration-300 ease-out",
                        isMobile ? "fixed left-0 top-[64px] h-[calc(100vh-64px)]" : "relative min-h-[calc(100vh-64px)]",
                        desktopWidthClass,
                        showDrawer ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0",
                    ].join(" ")}
                >
                    <div className="m-0 md:ml-1 h-full min-h-[calc(100vh-64px)] rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-3 shadow-[0_16px_32px_rgba(0,0,0,0.25)]">
                        <div className="mb-3 flex items-center justify-between border-b border-[var(--border-color)] pb-3">
                            {!isCollapsed && (
                                <h2 className="pl-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)]">Plans</h2>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    if (isMobile) {
                                        setIsMobileOpen(false);
                                        return;
                                    }
                                    setIsCollapsed((prev) => !prev);
                                }}
                                className="rounded-lg border border-[var(--border-strong)] p-1.5 text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-hover)]"
                                aria-label={sidebarToggleAriaLabel}
                            >
                                {sidebarToggleIcon}
                            </button>
                        </div>

                        {!isCollapsed && (
                            <>
                                <ul className="space-y-2">
                                    {currentPeriods.map((periodKey) => {
                                        const isSelected = selectedPlanRef.type === "static" && selectedPlanRef.key === periodKey;
                                        const plan = plansByPeriod[periodKey];

                                        // A plan is "Planned" only if it has focus items or any section with content
                                        const hasFocusItems = plan?.focusItems && plan.focusItems.length > 0;
                                        const hasSectionContent = Object.values(plan?.sections || {}).some(val => String(val || '').trim());
                                        const isPlanned = hasFocusItems || hasSectionContent;

                                        return (
                                            <li key={periodKey}>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedPlanRef({ type: "static", key: periodKey })}
                                                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                                                        isSelected
                                                            ? "border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]"
                                                            : "border-transparent bg-[var(--surface-deep)] text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-hover)]"
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span>{timePeriodLabels[periodKey]}</span>
                                                        {isPlanned && <span className="text-[10px] text-[var(--brand-coral)]">In Progress</span>}
                                                    </div>
                                                    <div className="mt-1 text-[11px] text-[var(--text-muted)]">{periodMetaByKey[periodKey] || ""}</div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="mb-2 mt-5 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text-muted)]">Custom Periods</div>
                                <button
                                    type="button"
                                    onClick={openCreateCustomPeriodModal}
                                    className="mb-2 flex w-full items-center gap-2 rounded-lg border border-dashed border-[var(--border-strong)] px-3 py-2 text-left text-sm text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)]"
                                >
                                    <AddIcon sx={{ fontSize: 14 }} />
                                     Custom Period
                                </button>
                                <ul className="space-y-2">
                                    {customPeriods.length ? customPeriods.map((period) => {
                                        const isSelected = selectedPlanRef.type === "custom" && selectedPlanRef.key === period.id;
                                        const dateText = period.fromDate && period.toDate
                                            ? ` (${period.fromDate} to ${period.toDate})`
                                            : "";

                                        // Check if plan has content
                                        const plan = period.plan || {};
                                        const hasFocusItems = plan.focusItems && plan.focusItems.length > 0;
                                        const hasSectionContent = Object.values(plan?.sections || {}).some(val => String(val || '').trim());
                                        const isPlanned = hasFocusItems || hasSectionContent;

                                        return (
                                            <li key={period.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedPlanRef({ type: "custom", key: period.id })}
                                                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                                                        isSelected
                                                            ? "border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]"
                                                            : "border-transparent bg-[var(--surface-deep)] text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-hover)]"
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span>{period.name}</span>
                                                        {isPlanned && <span className="text-[10px] text-[var(--brand-coral)]">Planned</span>}
                                                    </div>
                                                    {dateText && <div className="mt-1 text-[11px] text-[var(--text-muted)]">{dateText}</div>}
                                                </button>
                                            </li>
                                        );
                                    }) : (
                                        <li className="rounded-lg border border-transparent bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--text-muted)]">No custom periods yet.</li>
                                    )}
                                </ul>

                                <button
                                    type="button"
                                    onClick={() => setIsUpcomingExpanded((prev) => !prev)}
                                    className="mb-2 mt-5 flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]"
                                    aria-expanded={isUpcomingExpanded}
                                >
                                    <span>Upcoming</span>
                                    <ChevronDown
                                        size={15}
                                        className={`transition-transform ${isUpcomingExpanded ? "rotate-0" : "-rotate-90"}`}
                                    />
                                </button>
                                {isUpcomingExpanded && <ul className="space-y-2">
                                    {upcomingPeriods.map((periodKey) => {
                                        const isSelected = selectedPlanRef.type === "static" && selectedPlanRef.key === periodKey;
                                        const plan = plansByPeriod[periodKey];
                                        const hasFocusItems = plan?.focusItems && plan.focusItems.length > 0;
                                        const hasSectionContent = Object.values(plan?.sections || {}).some(val => String(val || '').trim());
                                        const isPlanned = hasFocusItems || hasSectionContent;

                                        return (
                                            <li key={periodKey}>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedPlanRef({ type: "static", key: periodKey })}
                                                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                                                        isSelected
                                                            ? "border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]"
                                                            : "border-transparent bg-[var(--surface-deep)] text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-hover)]"
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span>{timePeriodLabels[periodKey]}</span>
                                                        {isPlanned && <span className="text-[10px] text-[var(--brand-coral)]">In Progress</span>}
                                                    </div>
                                                    <div className="mt-1 text-[11px] text-[var(--text-muted)]">{periodMetaByKey[periodKey] || ""}</div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>}

                                <button
                                    type="button"
                                    onClick={() => setIsArchivedPlansExpanded((prev) => !prev)}
                                    className="mb-2 mt-5 flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]"
                                    aria-expanded={isArchivedPlansExpanded}
                                >
                                    <span>Recent/Archived Plans</span>
                                    <ChevronDown
                                        size={15}
                                        className={`transition-transform ${isArchivedPlansExpanded ? "rotate-0" : "-rotate-90"}`}
                                    />
                                </button>
                                {isArchivedPlansExpanded && <ul className="space-y-1">
                                    {archivedPlans.length ? archivedPlans.map((archived) => {
                                        const isSelected = selectedPlanRef.type === "archived" && selectedPlanRef.key === archived.id;
                                        return (
                                            <li key={archived.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedPlanRef({ type: "archived", key: archived.id });
                                                    }}
                                                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                                                        isSelected
                                                            ? "border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]"
                                                            : "border-transparent bg-[var(--surface-deep)] text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-hover)]"
                                                    }`}
                                                >
                                                    <div className="text-xs font-medium text-[var(--text-secondary)]">{archived.title || archived.id.slice(0, 8)}</div>
                                                    <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">{archived.subtitle || "Plan"}</div>
                                                </button>
                                            </li>
                                        );
                                    }) : (
                                        <li className="rounded-lg border border-transparent bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--text-muted)]">No archived plans.</li>
                                    )}
                                </ul>}
                            </>
                        )}
                    </div>
                </aside>

                <main className="flex-1 overflow-y-auto min-w-0 px-4 md:px-8">
                    <div className="mx-auto mt-4 flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                        <div className='inline-flex rounded-lg border border-[var(--border-color)] bg-[var(--surface-deep)] p-1'>
                            <button
                                type='button'
                                onClick={() => setContentView("details")}
                                className={`rounded-md px-3 py-1.5 text-sm transition ${
                                    contentView === "details"
                                        ? "bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]"
                                        : "text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]"
                                }`}
                            >
                                Details
                            </button>
                            <button
                                type='button'
                                onClick={() => setContentView("calendar")}
                                className={`rounded-md px-3 py-1.5 text-sm transition ${
                                    contentView === "calendar"
                                        ? "bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]"
                                        : "text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]"
                                }`}
                            >
                                Calendar
                            </button>
                        </div>

                        <div className='ml-auto flex gap-3 items-center'>
                            {isArchivedView ? (
                                <span className="rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-4 py-2 text-sm font-medium text-[var(--text-muted)]">
                                    {isLoadingArchivedPlan ? "Loading..." : "Read-only"}
                                </span>
                            ) : isViewMode ? (
                                <button
                                    onClick={() => setIsViewMode(false)}
                                    className="inline-flex items-center gap-2 rounded-md border border-[var(--button-primary-bg)] bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-text)] shadow-sm transition hover:brightness-105 active:brightness-95"
                                >
                                    Edit
                                </button>
                            ) : (
                                <button
                                    onClick={handleSavePlan}
                                    disabled={isSavingPlanner}
                                    className="inline-flex items-center gap-2 rounded-md bg-[var(--surface-deep)] hover:bg-[var(--surface-deep)] active:bg-[var(--surface-hover)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] border border-[var(--border-color)] shadow-sm shadow-black/40 transition"
                                >
                                    {isSavingPlanner ? "Saving..." : "Save"}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className='mx-auto mt-4 w-full max-w-5xl text-[var(--text-primary)] text-3xl md:text-4xl'>
                        {planDateDisplay}
                    </div>
                    <div className='mx-auto mt-8 w-full max-w-5xl pb-10'>
                        <div className='rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-5'>
                            <div className='flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-color)] pb-4'>
                                <h2 className='text-xl md:text-2xl font-semibold text-[var(--text-primary)]'>{planTitle || "Untitled Plan"}</h2>
                                <span className='rounded-full border border-[var(--brand-coral)]/40 bg-[var(--brand-coral)]/10 px-3 py-1 text-xs font-medium text-[var(--brand-coral)]'>
                                    {timePeriodLabels[effectivePeriod] || "Custom"}
                                </span>
                            </div>

                            {!activePlan && (
                                <p className='mt-4 rounded-lg border border-dashed border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-muted)]'>
                                    This period does not have a plan yet. Use Add Plan to create one.
                                </p>
                            )}

                            {/* ── VIEW MODE ───────────────────────────────────────── */}
                            {contentView === "calendar" ? (
                                <PlannerCalendarView
                                    effectivePeriod={effectivePeriod}
                                    planSectionsData={planSectionsData}
                                    planFocusItems={planFocusItems}
                                    supportingWeekBreakdown={calendarSupportingWeekBreakdown}
                                    supportingWeekPeriodKey={calendarSupportingWeekPeriodKey}
                                    tasksList={calendarTasksList}
                                    projectsList={projectsList}
                                    now={now}
                                    fromDate={activePlan?.fromDate || null}
                                    toDate={activePlan?.toDate || null}
                                    planDate={calendarPlanDate}
                                    isReadOnly={isArchivedView}
                                    onPlannerScheduleMove={handleCalendarPlannerScheduleMove}
                                    onTaskDeadlineMove={handleCalendarTaskDeadlineMove}
                                    onDateDoubleClick={handleCalendarDateDoubleClick}
                                    onTodayClick={handleCalendarTodayClick}
                                />
                            ) : (isViewMode || isArchivedView) ? (
                                <div className='mt-5 space-y-5'>
                                    {isWeeklyPlan && String(planSectionsData.weeklyOutcomes || "").trim() && (
                                        <div className='rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                            <p className='mb-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--brand-coral)]'>1. Weekly Outcomes</p>
                                            <p className='whitespace-pre-wrap text-sm text-[var(--text-primary)]'>{planSectionsData.weeklyOutcomes}</p>
                                        </div>
                                    )}



                                            {isDayPlan && planSectionsData.mainOutcome && (
                                        <div className='rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                            <p className='mb-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--brand-coral)]'>Main Outcome</p>
                                            <p className='text-sm text-[var(--text-primary)]'>{planSectionsData.mainOutcome}</p>
                                        </div>
                                    )}

                                    {planFocusItems.length > 0 && (
                                        <div className='rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                            <p className='mb-3 text-[11px] font-semibold uppercase tracking-widest text-[var(--brand-coral)]'>{isWeeklyPlan ? "2. Focus Areas" : "What I'll work on"}</p>
                                            <div className='space-y-2'>
                                                {planFocusItems.map((item) => {
                                                    const pomodorosNum = Number(item.plannedPomodoros) || 0;
                                                    const hoursNum = Number(item.plannedHours) || 0;
                                                    const hasTimeRange = item.startTime && item.endTime;
                                                    const targetValueNum = Number(item.targetValue) || 0;
                                                    const targetUnit = item.targetUnit || "pomodoros";
                                                    return (
                                                        <div key={item.id} className='flex items-start justify-between gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--surface-deep)] px-3 py-2'>
                                                            <div className='flex items-center gap-2 min-w-0'>
                                                                <span className='shrink-0 rounded bg-[var(--brand-coral)]/20 px-1.5 py-0.5 text-[10px] font-medium text-[var(--brand-coral)]'>{item.type}</span>
                                                                <span className='truncate text-sm text-[var(--text-primary)]'>{item.title || <span className='italic text-[var(--text-muted)]'>Untitled</span>}</span>
                                                            </div>
                                                            <div className='flex shrink-0 items-center gap-2'>
                                                                {!isWeeklyPlan && hasTimeRange && (
                                                                    <span className='rounded bg-[var(--brand-coral)]/20 px-2 py-0.5 text-[11px] font-medium text-[var(--brand-coral)]'>
                                                                        {item.startTime} – {item.endTime}
                                                                    </span>
                                                                )}
                                                                {isWeeklyPlan && targetValueNum > 0 && (
                                                                    <span className='rounded bg-emerald-500/20 px-2 py-0.5 text-[11px] font-medium text-emerald-300'>
                                                                        Target: {targetValueNum} {targetUnit}
                                                                    </span>
                                                                )}
                                                                {!isWeeklyPlan && pomodorosNum > 0 && (
                                                                    <span className='rounded bg-orange-500/20 px-2 py-0.5 text-[11px] font-medium text-orange-300'>
                                                                        🍅 {pomodorosNum}
                                                                    </span>
                                                                )}
                                                                {!isWeeklyPlan && hoursNum > 0 && (
                                                                    <span className='rounded bg-sky-500/20 px-2 py-0.5 text-[11px] font-medium text-sky-300'>
                                                                        ⏱ {hoursNum}h
                                                                    </span>
                                                                )}
                                                                <button
                                                                    type='button'
                                                                    onClick={() => startSessionForItem(item)}
                                                                    title='Start a work session'
                                                                    className='flex items-center gap-1 rounded bg-green-600/20 px-2 py-0.5 text-[11px] font-medium text-green-400 transition hover:bg-green-600/40'
                                                                >
                                                                    <Play size={10} />
                                                                    Start session
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {!isWeeklyPlan && (totalPomodoros > 0 || totalHours > 0 || completedPomodoros > 0) && (
                                                <div className='mt-3 flex items-center gap-3 border-t border-[var(--border-color)] pt-3'>
                                                    <span className='text-[11px] text-[var(--text-muted)]'>Totals:</span>
                                                    {totalPomodoros > 0 && (
                                                        <span className='rounded bg-orange-500/10 px-2 py-0.5 text-[11px] font-medium text-orange-300'>🍅 {totalPomodoros} planned</span>
                                                    )}
                                                    {completedPomodoros > 0 && (
                                                        <span className='rounded bg-green-500/10 px-2 py-0.5 text-[11px] font-medium text-green-300'>✅ {completedPomodoros} done</span>
                                                    )}
                                                    {totalHours > 0 && (
                                                        <span className='rounded bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-300'>⏱ {totalHours}h</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {planSectionsData.reviewReflection && (
                                        <div className='rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                            <p className='mb-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--brand-coral)]'>Notes / Reflection</p>
                                            <p className='whitespace-pre-wrap text-sm text-[var(--text-primary)]'>{planSectionsData.reviewReflection}</p>
                                        </div>
                                    )}

                                    {!isDayPlan && planSections
                                        .filter((section) => section.id !== "reviewReflection" && section.id !== "weeklyOutcomes" && section.id !== "priorities")
                                        .map((section) => {
                                            const value = planSectionsData[section.id];
                                            const hasListValue = Array.isArray(value) && value.length > 0;
                                            const hasTextValue = !Array.isArray(value) && String(value || "").trim();
                                            const hasDayMapValue = section.inputType === "dayAllocation"
                                                && value
                                                && weekdayKeys.some((day) => String(value?.[day] || "").trim());
                                            const hasWeekBreakdownValue = section.inputType === "weekBreakdown"
                                                && value
                                                && fullWeekdayKeys.some((day) => Array.isArray(value?.[day]) && value[day].length > 0);
                                            const hasMonthlyWeekBreakdownValue = section.inputType === "monthlyWeeklyBreakdown"
                                                && value
                                                && monthlyWeekKeys.some((weekKey) => Array.isArray(value?.[weekKey]) && value[weekKey].length > 0);

                                            if (!hasListValue && !hasTextValue && !hasDayMapValue && !hasWeekBreakdownValue && !hasMonthlyWeekBreakdownValue) return null;

                                            return (
                                                <div key={`view-${section.id}`} className='rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                                    <p className='mb-2 text-[11px] font-semibold uppercase tracking-widest text-[var(--brand-coral)]'>{section.label}</p>
                                                    {section.inputType === "dayAllocation" ? (
                                                        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                                                            {weekdayKeys.map((day) => (
                                                                <div key={`${section.id}-${day}`} className='rounded border border-[var(--border-color)] bg-[var(--surface-deep)] px-3 py-2'>
                                                                    <p className='text-[11px] font-semibold text-[var(--brand-coral)]'>{day}</p>
                                                                    <p className='mt-1 text-sm text-[var(--text-primary)]'>{value?.[day] || "-"}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : section.inputType === "weekBreakdown" ? (
                                                        <div className='space-y-3'>
                                                            {fullWeekdayKeys.map((day) => {
                                                                const dayItems = Array.isArray(value?.[day]) ? value[day] : [];
                                                                return (
                                                                    <div key={`${section.id}-${day}`} className='rounded-lg border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                                                        <p className='mb-3 text-sm font-semibold text-[var(--brand-coral)]'>{day}</p>
                                                                        {dayItems.length > 0 ? (
                                                                            <div className='space-y-2'>
                                                                                {dayItems.map((item, idx) => (
                                                                                    <div key={`${day}-${idx}`} className='flex items-center justify-between gap-3 rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-3 py-2'>
                                                                                        <div className='flex items-center gap-2 min-w-0'>
                                                                                            <span className='shrink-0 rounded bg-[var(--brand-coral)]/20 px-1.5 py-0.5 text-[10px] font-medium text-[var(--brand-coral)]'>
                                                                                                {typeof item === "string" ? "Custom" : item.type}
                                                                                            </span>
                                                                                            <span className='truncate text-sm text-[var(--text-primary)]'>
                                                                                                {typeof item === "string" ? item : item.title}
                                                                                            </span>
                                                                                        </div>
                                                                                        {typeof item !== "string" && item.startTime && item.endTime && (
                                                                                            <span className='shrink-0 text-[11px] text-[var(--text-muted)]'>
                                                                                                {item.startTime} – {item.endTime}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <p className='text-xs text-[var(--text-muted)] italic'>No items scheduled</p>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : section.inputType === "monthlyWeeklyBreakdown" ? (
                                                        <div className='space-y-3'>
                                                            {monthlyWeekKeys.map((weekKey) => {
                                                                const weekItems = Array.isArray(value?.[weekKey]) ? value[weekKey] : [];
                                                                return (
                                                                    <div key={`${section.id}-${weekKey}`} className='rounded-lg border border-[var(--border-color)] bg-[var(--surface-deep)] p-3'>
                                                                        <p className='text-xs font-semibold uppercase tracking-wide text-[var(--brand-coral)]'>
                                                                            {weekKey}
                                                                            <span className='ml-2 text-[11px] font-medium normal-case text-[var(--text-muted)]'>
                                                                                {monthlyWeekRangeLabels[weekKey]}
                                                                            </span>
                                                                        </p>
                                                                        {weekItems.length > 0 ? (
                                                                            <div className='mt-2 space-y-1'>
                                                                                {weekItems.map((item, idx) => (
                                                                                    <p key={`${weekKey}-${idx}`} className='text-sm text-[var(--text-primary)]'>• {item}</p>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <p className='mt-2 text-xs italic text-[var(--text-muted)]'>No plan yet</p>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : section.inputType === "checklist" ? (
                                                        <div className='space-y-2'>
                                                            {(Array.isArray(value) ? value : []).map((item) => {
                                                                const label = typeof item === "string" ? item : item.label;
                                                                const checked = typeof item === "string" ? false : !!item.checked;
                                                                return (
                                                                    <div key={item.id || label} className='flex items-center gap-2 rounded border border-[var(--border-color)] bg-[var(--surface-deep)] px-3 py-2'>
                                                                        <span className={`text-xs ${checked ? "text-emerald-300" : "text-[var(--text-muted)]"}`}>{checked ? "[x]" : "[ ]"}</span>
                                                                        <span className={`text-sm ${checked ? "text-[var(--text-muted)] line-through" : "text-[var(--text-primary)]"}`}>{label}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : section.inputType === "learningLinks" ? (
                                                        <div className='space-y-2'>
                                                            {(Array.isArray(value) ? value : []).map((item) => (
                                                                <div key={item.id || `${item.type}-${item.title}`} className='flex items-center gap-2 rounded border border-[var(--border-color)] bg-[var(--surface-deep)] px-3 py-2'>
                                                                    <span className='rounded bg-[var(--brand-coral)]/20 px-1.5 py-0.5 text-[10px] font-medium text-[var(--brand-coral)]'>
                                                                        {item.type || "Custom"}
                                                                    </span>
                                                                    <span className='text-sm text-[var(--text-primary)]'>{item.title || item.label}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : hasListValue ? (
                                                        <div className='flex flex-wrap gap-2'>
                                                            {value.map((item) => (
                                                                <span key={item.id} className='rounded bg-[var(--brand-coral)]/20 px-2 py-1 text-xs text-[var(--brand-coral)]'>
                                                                    {item.label}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className='whitespace-pre-wrap text-sm text-[var(--text-primary)]'>{value}</p>
                                                    )}
                                                </div>
                                            );
                                        })}

                                    {!planSectionsData.mainOutcome && planFocusItems.length === 0 && !planSectionsData.reviewReflection && (
                                        <p className='text-sm text-[var(--text-muted)] italic'>No plan content yet. Click Edit to start planning.</p>
                                    )}
                                </div>
                            ) : (
                                /* ── EDIT MODE ────────────────────────────────────── */
                                <>
                                    {isWeeklyPlan && (
                                        <div className='mt-5 rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                            <h4 className='mb-2 text-sm font-semibold text-[var(--text-primary)]'>1. Weekly Outcomes</h4>
                                            <textarea
                                                value={planSectionsData.weeklyOutcomes || ""}
                                                onChange={(e) => updateInlineSection("weeklyOutcomes", e.target.value)}
                                                placeholder='1-3 outcomes that make this week successful...'
                                                className='h-24 w-full resize-none rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                            />
                                        </div>
                                    )}

                                    {isDayPlan && (
                                        <div className='mt-5 rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                            <h4 className='mb-2 text-sm font-semibold text-[var(--text-primary)]'>Main Outcome</h4>
                                            <input
                                                type='text'
                                                value={planSectionsData.mainOutcome || ""}
                                                onChange={(e) => updateInlineSection("mainOutcome", e.target.value)}
                                                placeholder='What is your main focus / outcome for today?'
                                                className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                            />
                                        </div>
                                    )}

                                    {isMonthlyPlan && (
                                        <div className='mt-5 rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                            <h4 className='mb-2 text-sm font-semibold text-[var(--text-primary)]'>Monthly priorities</h4>
                                            <textarea
                                                value={planSectionsData.priorities || ""}
                                                onChange={(e) => updateInlineSection("priorities", e.target.value)}
                                                placeholder='Add details...'
                                                className='h-24 w-full resize-none rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                            />
                                        </div>
                                    )}

                                    <div className='mt-5'>
                                        <h3 className='mb-3 text-sm font-semibold uppercase tracking-wide text-[var(--text-muted)]'>{isWeeklyPlan ? "2. Focus Areas" : "What will you work on?"}</h3>
                                        <div className='space-y-3'>
                                            {(planFocusItems || []).map((item) => {
                                                const getItemsForType = () => {
                                                    if (item.type === "Task") return tasksList;
                                                    if (item.type === "Project") return projectsList;
                                                    return [];
                                                };
                                                const availableItems = getItemsForType();
                                                const hasItems = availableItems && availableItems.length > 0;

                                                return (
                                                    <div key={item.id || `${item.type}-${item.title}`} className='space-y-2'>
                                                        <div className='flex items-start gap-2'>
                                                            <div className='relative'>
                                                                <button
                                                                    type='button'
                                                                    onClick={() => toggleInlineFocusMenu(item.id)}
                                                                    className='flex items-center gap-1 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)]'
                                                                >
                                                                    {item.type}
                                                                    <ChevronDown size={12} className={expandedFocusMenus[item.id] ? "rotate-180" : ""} />
                                                                </button>
                                                                {expandedFocusMenus[item.id] && (
                                                                    <div className='absolute left-0 top-full z-20 mt-1 min-w-[140px] rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] shadow-lg'>
                                                                        {focusItemTypes.map((type) => (
                                                                            <button
                                                                                key={type}
                                                                                type='button'
                                                                                onClick={() => selectInlineFocusType(item.id, type)}
                                                                                className='block w-full px-3 py-2 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--surface-hover)]'
                                                                            >
                                                                                {type}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className='flex-1 relative'>
                                                                {hasItems ? (
                                                                    <div className='relative'>
                                                                        <button
                                                                            type='button'
                                                                            onClick={() => setExpandedFocusOptions((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                                                                            className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] text-left transition hover:bg-[var(--surface-hover)]'
                                                                        >
                                                                            {item.title || `Select ${item.type}...`}
                                                                        </button>
                                                                        {expandedFocusOptions[item.id] && (
                                                                            <div className='absolute left-0 top-full z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] shadow-lg'>
                                                                                {availableItems.length > 0 ? (
                                                                                    availableItems.map((availItem) => {
                                                                                        const displayName = item.type === "Task"
                                                                                            ? (availItem.text || availItem.title || availItem.name)
                                                                                            : (availItem.name || availItem.title);

                                                                                        return (
                                                                                            <button
                                                                                                key={availItem.id}
                                                                                                type='button'
                                                                                                onClick={() => selectItemFromOptions(item.id, displayName, availItem.id)}
                                                                                                className='block w-full border-b border-[var(--border-color)] px-3 py-2 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--surface-hover)] last:border-b-0'
                                                                                            >
                                                                                                {displayName}
                                                                                            </button>
                                                                                        );
                                                                                    })
                                                                                ) : (
                                                                                    <div className='px-3 py-2 text-center text-xs text-[var(--text-muted)]'>
                                                                                        No {item.type.toLowerCase()}s available
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <input
                                                                        type='text'
                                                                        value={item.title || ""}
                                                                        onChange={(e) => updateInlineFocusItem(item.id, { title: e.target.value })}
                                                                        placeholder='Add focus item...'
                                                                        className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                    />
                                                                )}

                                                                {isWeeklyPlan ? (
                                                                    <div className='mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_160px]'>
                                                                        <input
                                                                            type='number'
                                                                            min='0'
                                                                            value={item.targetValue ?? ""}
                                                                            onChange={(e) => updateInlineFocusItem(item.id, { targetValue: e.target.value })}
                                                                            placeholder='Target value'
                                                                            className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                        />
                                                                        <select
                                                                            value={item.targetUnit || "pomodoros"}
                                                                            onChange={(e) => updateInlineFocusItem(item.id, { targetUnit: e.target.value })}
                                                                            className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                        >
                                                                            <option value='pomodoros'>pomodoros</option>
                                                                            <option value='sessions'>sessions</option>
                                                                            <option value='hours'>hours</option>
                                                                        </select>
                                                                    </div>
                                                                ) : (
                                                                    <div className='mt-2 space-y-2'>
                                                                        {item.title && (
                                                                            <div className='space-y-1'>
                                                                                <label className='block text-[11px] font-medium text-[var(--text-muted)]'>Time range (optional)</label>
                                                                                <WeekTimeRangePicker
                                                                                    startTime={item.startTime || ""}
                                                                                    endTime={item.endTime || ""}
                                                                                    onChange={(startTime, endTime) => updateInlineFocusItem(item.id, { startTime, endTime })}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                                                                            <input
                                                                                type='number'
                                                                                min='0'
                                                                                value={item.plannedPomodoros ?? ""}
                                                                                onChange={(e) => updateInlineFocusItem(item.id, { plannedPomodoros: e.target.value, plannedHours: e.target.value ? "" : item.plannedHours })}
                                                                                placeholder='Pomodoro target'
                                                                                className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                            />
                                                                            <input
                                                                                type='number'
                                                                                min='0'
                                                                                step='0.5'
                                                                                value={item.plannedHours ?? ""}
                                                                                onChange={(e) => updateInlineFocusItem(item.id, { plannedHours: e.target.value, plannedPomodoros: e.target.value ? "" : item.plannedPomodoros })}
                                                                                placeholder='Hours target'
                                                                                className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>


                                                            <button
                                                                type='button'
                                                                onClick={() => removeInlineFocusItem(item.id)}
                                                                className='flex-shrink-0 rounded-md p-2 text-[var(--text-muted)] transition hover:bg-[var(--surface-hover)] hover:text-red-400'
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            <button
                                                type='button'
                                                onClick={addInlineFocusItem}
                                                className='flex items-center gap-2 text-xs text-[var(--brand-coral)] transition hover:text-[var(--brand-coral)]'
                                            >
                                                <Plus size={14} />
                                                Add focus item
                                            </button>
                                        </div>
                                    </div>

                                    <div className='mt-6 grid grid-cols-1 gap-4 md:grid-cols-2'>
                                        {sectionsToRender.map((section) => {
                                            const value = planSectionsData[section.id]
                                                || (section.valueType === "dailyMap"
                                                    ? createWeekdayMap()
                                                    : section.valueType === "weeklyListMap"
                                                        ? createMonthlyWeekMap()
                                                        : "");
                                            const inputType = section.inputType || "textarea";

                                                return (
                                                        <div key={section.id} className={`rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4 ${section.fullWidth ? 'md:col-span-2' : ''}`}>
                                                    <h4 className='mb-2 text-sm font-semibold text-[var(--text-primary)]'>{section.label}</h4>
                                                    {inputType === "textarea" ? (
                                                        <textarea
                                                            value={value}
                                                            onChange={(e) => updateInlineSection(section.id, e.target.value)}
                                                            placeholder={section.placeholder || "Add details..."}
                                                            className='h-24 w-full resize-none rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                        />
                                                    ) : inputType === "projectMultiSelect" ? (
                                                        <div className='space-y-3'>
                                                            <div className='max-h-52 space-y-2 overflow-y-auto rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] p-2'>
                                                                {projectsList.length ? projectsList.map((project) => {
                                                                    const projectId = String(project.id);
                                                                    const projectLabel = project.name || project.title || `Project #${project.id}`;
                                                                    const selected = (Array.isArray(value) ? value : []).some((item) => String(item.id) === projectId);
                                                                    return (
                                                                        <button
                                                                            key={projectId}
                                                                            type='button'
                                                                            onClick={() => toggleMonthlyProject(project)}
                                                                            className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition ${
                                                                                selected
                                                                                    ? 'border-[var(--brand-coral)]/50 bg-[var(--brand-coral)]/10 text-[var(--brand-coral)]'
                                                                                    : 'border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]'
                                                                            }`}
                                                                        >
                                                                            <span className='truncate'>{projectLabel}</span>
                                                                            <span className='ml-3 text-xs'>{selected ? 'Selected' : 'Select'}</span>
                                                                        </button>
                                                                    );
                                                                }) : (
                                                                    <p className='px-2 py-2 text-xs text-[var(--text-muted)]'>No projects found.</p>
                                                                )}
                                                            </div>
                                                            <div className='flex flex-wrap gap-2'>
                                                                {(Array.isArray(value) ? value : []).map((item) => (
                                                                    <span key={item.id} className='rounded bg-[var(--brand-coral)]/20 px-2 py-1 text-xs text-[var(--brand-coral)]'>
                                                                        {item.label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : inputType === "checklist" ? (
                                                        <div className='space-y-2'>
                                                            <div className='space-y-2'>
                                                                {(Array.isArray(value) ? value : []).map((item) => {
                                                                    const itemId = item.id || String(item);
                                                                    const label = typeof item === "string" ? item : item.label;
                                                                    const checked = typeof item === "string" ? false : !!item.checked;
                                                                    return (
                                                                        <div key={itemId} className='flex items-center gap-2 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2'>
                                                                            <button
                                                                                type='button'
                                                                                onClick={() => toggleChecklistItem(section.id, itemId)}
                                                                                className={`h-4 w-4 rounded border text-[10px] ${checked ? 'border-emerald-500 bg-emerald-500/20 text-emerald-300' : 'border-[var(--border-strong)] text-transparent'}`}
                                                                            >
                                                                                ✓
                                                                            </button>
                                                                            <span className={`flex-1 text-sm ${checked ? 'text-[var(--text-muted)] line-through' : 'text-[var(--text-primary)]'}`}>{label}</span>
                                                                            <button
                                                                                type='button'
                                                                                onClick={() => removeChecklistItem(section.id, itemId)}
                                                                                className='text-[var(--text-muted)] transition hover:text-red-400'
                                                                            >
                                                                                <X size={12} />
                                                                            </button>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className='flex gap-2'>
                                                                <input
                                                                    type='text'
                                                                    value={checklistInputs[section.id] || ""}
                                                                    onChange={(e) => setChecklistInputs((prev) => ({ ...prev, [section.id]: e.target.value }))}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            addChecklistItem(section.id);
                                                                        }
                                                                    }}
                                                                    placeholder='Add milestone...'
                                                                    className='flex-1 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                />
                                                                <button
                                                                    type='button'
                                                                    onClick={() => addChecklistItem(section.id)}
                                                                    className='rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--brand-coral)] transition hover:bg-[var(--surface-hover)]'
                                                                >
                                                                    Add
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : inputType === "learningLinks" ? (
                                                        <div className='space-y-3'>
                                                            <div className='space-y-2'>
                                                                {(Array.isArray(value) ? value : []).map((item) => (
                                                                    <div key={item.id || `${item.type}-${item.title}`} className='flex items-center justify-between gap-2 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2'>
                                                                        <div className='flex items-center gap-2 min-w-0'>
                                                                            <span className='rounded bg-[var(--brand-coral)]/20 px-1.5 py-0.5 text-[10px] font-medium text-[var(--brand-coral)]'>
                                                                                {item.type || "Custom"}
                                                                            </span>
                                                                            <span className='truncate text-sm text-[var(--text-primary)]'>{item.title || item.label}</span>
                                                                        </div>
                                                                        <button
                                                                            type='button'
                                                                            onClick={() => removeLearningObjectiveItem(section.id, item.id)}
                                                                            className='text-[var(--text-muted)] transition hover:text-red-400'
                                                                        >
                                                                            <X size={12} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className='space-y-2 rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] p-3'>
                                                                <div className='flex gap-2'>
                                                                    <select
                                                                        value={learningObjectiveFormState.type}
                                                                        onChange={(e) => setLearningObjectiveFormState({ type: e.target.value, title: "", refId: null })}
                                                                        className='w-[140px] rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                    >
                                                                        {learningObjectiveTypes.map((type) => (
                                                                            <option key={type} value={type}>{type}</option>
                                                                        ))}
                                                                    </select>
                                                                    {(() => {
                                                                        const availableLearningItems = getLearningItemsForType(learningObjectiveFormState.type);
                                                                        if (availableLearningItems.length > 0 && learningObjectiveFormState.type !== "Custom") {
                                                                            return (
                                                                                <select
                                                                                    value={learningObjectiveFormState.title}
                                                                                    onChange={(e) => {
                                                                                        const selectedItem = availableLearningItems.find((item) => item.label === e.target.value);
                                                                                        setLearningObjectiveFormState((prev) => ({
                                                                                            ...prev,
                                                                                            title: e.target.value,
                                                                                            refId: selectedItem?.id ?? null,
                                                                                        }));
                                                                                    }}
                                                                                    className='flex-1 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                                >
                                                                                    <option value=''>Select {learningObjectiveFormState.type.toLowerCase()}...</option>
                                                                                    {availableLearningItems.map((item) => (
                                                                                        <option key={item.id} value={item.label}>{item.label}</option>
                                                                                    ))}
                                                                                </select>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <input
                                                                                type='text'
                                                                                value={learningObjectiveFormState.title}
                                                                                onChange={(e) => setLearningObjectiveFormState((prev) => ({ ...prev, title: e.target.value, refId: null }))}
                                                                                placeholder='Add learning objective...'
                                                                                className='flex-1 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                            />
                                                                        );
                                                                    })()}
                                                                </div>
                                                                <button
                                                                    type='button'
                                                                    onClick={() => addLearningObjectiveItem(section.id)}
                                                                    disabled={!String(learningObjectiveFormState.title || "").trim()}
                                                                    className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-xs text-[var(--brand-coral)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-50'
                                                                >
                                                                    Add learning objective
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : inputType === "dayAllocation" ? (
                                                        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                                                            {weekdayKeys.map((day) => (
                                                                <div key={`${section.id}-${day}`} className='rounded border border-[var(--border-color)] bg-[var(--surface-deep)] p-2'>
                                                                    <label className='mb-1 block text-[11px] font-semibold text-[var(--brand-coral)]'>{day}</label>
                                                                    <input
                                                                        type={section.dayValueType === "number" ? "number" : "text"}
                                                                        min={section.dayValueType === "number" ? "0" : undefined}
                                                                        value={value?.[day] || ""}
                                                                        onChange={(e) => updateDayAllocationEntry(section.id, day, e.target.value)}
                                                                        placeholder={section.placeholder || "Add details..."}
                                                                        className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : inputType === "weekBreakdown" ? (
                                                        <div className='space-y-4'>
                                                            {fullWeekdayKeys.map((day) => {
                                                                const dayItems = Array.isArray(value?.[day]) ? value[day] : [];
                                                                const formState = weekBreakdownFormState[day] || { type: "Task", title: "", time: "" };
                                                                const availableItems = getItemsForBreakdownType(formState.type);
                                                                const hasItems = availableItems && availableItems.length > 0;

                                                                return (
                                                                    <div key={`${section.id}-${day}`} className='rounded-lg border border-[var(--border-color)] bg-[var(--surface-deep)] p-4'>
                                                                        <p className='mb-3 text-sm font-semibold text-[var(--brand-coral)]'>{day}</p>

                                                                        <div className='mb-2 space-y-1'>
                                                                            {dayItems.map((itemText, idx) => (
                                                                                 <div key={`${day}-${idx}`} className='flex items-center justify-between gap-2 rounded bg-[var(--surface-deep)] px-3 py-2'>
                                                                                     <span className='text-xs text-[var(--text-primary)]'>
                                                                                         {typeof itemText === "string"
                                                                                             ? itemText
                                                                                             : `${itemText.startTime && itemText.endTime ? `${itemText.startTime} - ${itemText.endTime} | ` : ""}${itemText.type}: ${itemText.title}`}
                                                                                     </span>
                                                                                     <button
                                                                                         type='button'
                                                                                         onClick={() => removeWeekBreakdownScheduleItem(day, idx)}
                                                                                         className='text-[var(--text-muted)] transition hover:text-red-400'
                                                                                         aria-label={`Remove ${day} breakdown item`}
                                                                                     >
                                                                                         <X size={12} />
                                                                                     </button>
                                                                                 </div>
                                                                             ))}
                                                                        </div>

                                                                        <div className='space-y-2 rounded border border-[var(--border-color)] bg-[var(--surface-deep)] p-3'>
                                                                            <div className='flex items-center gap-2'>
                                                                                <select
                                                                                    value={formState.type}
                                                                                    onChange={(e) => setWeekBreakdownFormState((prev) => ({
                                                                                        ...prev,
                                                                                        [day]: { ...prev[day], type: e.target.value, title: "", startTime: "", endTime: "" },
                                                                                    }))}
                                                                                    className='w-[100px] rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                                >
                                                                                    {focusItemTypes.map((type) => (
                                                                                        <option key={type} value={type}>{type}</option>
                                                                                    ))}
                                                                                </select>

                                                                                {hasItems ? (
                                                                                    <select
                                                                                        value={formState.title}
                                                                                        onChange={(e) => setWeekBreakdownFormState((prev) => ({
                                                                                            ...prev,
                                                                                            [day]: { ...prev[day], title: e.target.value },
                                                                                        }))}
                                                                                        className='flex-1 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                                    >
                                                                                        <option value=''>Select {formState.type.toLowerCase()}...</option>
                                                                                        {availableItems.map((item) => {
                                                                                            const displayName = formState.type === "Task"
                                                                                                ? (item.text || item.title || item.name)
                                                                                                : (item.name || item.title);
                                                                                            return (
                                                                                                <option key={item.id} value={displayName}>{displayName}</option>
                                                                                            );
                                                                                        })}
                                                                                    </select>
                                                                                ) : (
                                                                                    <input
                                                                                        type='text'
                                                                                        value={formState.title}
                                                                                        onChange={(e) => setWeekBreakdownFormState((prev) => ({
                                                                                            ...prev,
                                                                                            [day]: { ...prev[day], title: e.target.value },
                                                                                        }))}
                                                                                        placeholder='Item name...'
                                                                                        className='flex-1 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                                    />
                                                                                )}
                                                                            </div>

                                                                            {formState.title && (
                                                                                <div className='space-y-1'>
                                                                                    <label className='block text-[11px] font-medium text-[var(--text-muted)]'>Time range (optional)</label>
                                                                                    <WeekTimeRangePicker
                                                                                        startTime={formState.startTime}
                                                                                        endTime={formState.endTime}
                                                                                        onChange={(startTime, endTime) => setWeekBreakdownFormState((prev) => ({
                                                                                            ...prev,
                                                                                            [day]: { ...prev[day], startTime, endTime },
                                                                                        }))}
                                                                                    />
                                                                                </div>
                                                                            )}

                                                                            {(() => {
                                                                                const isValid = isWeekBreakdownTimeRangeValid(formState);
                                                                                return (
                                                                                    <button
                                                                                        type='button'
                                                                                        onClick={() => addWeekBreakdownScheduleItem(day)}
                                                                                        disabled={!isValid}
                                                                                        className={`w-full rounded-md border px-2 py-2 text-xs font-medium transition ${
                                                                                            isValid
                                                                                                ? 'border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] hover:bg-[var(--surface-hover)] hover:text-[var(--brand-coral)] cursor-pointer'
                                                                                                : 'border-[var(--border-color)] bg-[var(--surface-deep)] text-[var(--text-muted)] cursor-not-allowed'
                                                                                        }`}
                                                                                    >
                                                                                        + Add
                                                                                    </button>
                                                                                );
                                                                            })()}
                                                                         </div>
                                                                     </div>
                                                                 );
                                                             })}
                                                         </div>
                                                    ) : inputType === "monthlyWeeklyBreakdown" ? (
                                                        <div className='space-y-3'>
                                                            {monthlyWeekKeys.map((weekKey) => {
                                                                const weekItems = Array.isArray(value?.[weekKey]) ? value[weekKey] : [];
                                                                return (
                                                                    <div key={`${section.id}-${weekKey}`} className='rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] p-3'>
                                                                        <p className='text-xs font-semibold uppercase tracking-wide text-[var(--brand-coral)]'>
                                                                            {weekKey}
                                                                            <span className='ml-2 text-[11px] font-medium normal-case text-[var(--text-muted)]'>
                                                                                {monthlyWeekRangeLabels[weekKey]}
                                                                            </span>
                                                                        </p>
                                                                        <div className='mt-2 space-y-1'>
                                                                            {weekItems.map((itemText, idx) => (
                                                                                <div key={`${weekKey}-${idx}`} className='flex items-center justify-between gap-2 rounded bg-[var(--surface-deep)] px-2 py-1.5'>
                                                                                    <span className='text-xs text-[var(--text-primary)]'>{itemText}</span>
                                                                                    <button
                                                                                        type='button'
                                                                                        onClick={() => removeMonthlyWeekItem(section.id, weekKey, idx)}
                                                                                        className='text-[var(--text-muted)] transition hover:text-red-400'
                                                                                    >
                                                                                        <X size={12} />
                                                                                    </button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                        <div className='mt-2 flex gap-2'>
                                                                            <input
                                                                                type='text'
                                                                                value={monthlyWeeklyInputs[weekKey] || ""}
                                                                                onChange={(e) => setMonthlyWeeklyInputs((prev) => ({ ...prev, [weekKey]: e.target.value }))}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        e.preventDefault();
                                                                                        addMonthlyWeekItem(section.id, weekKey);
                                                                                    }
                                                                                }}
                                                                                placeholder='Add weekly focus...'
                                                                                className='flex-1 rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                                            />
                                                                            <button
                                                                                type='button'
                                                                                onClick={() => addMonthlyWeekItem(section.id, weekKey)}
                                                                                className='rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-2 py-2 text-xs text-[var(--brand-coral)] transition hover:bg-[var(--surface-hover)]'
                                                                            >
                                                                                Add
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type={inputType}
                                                            min={inputType === "number" ? "0" : undefined}
                                                            value={value}
                                                            onChange={(e) => updateInlineSection(section.id, e.target.value)}
                                                            placeholder={section.placeholder || "Add details..."}
                                                            className='w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)]'
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </main>
            </div>

            {isAddPlanModalOpen && (
                <AddPlanModal
                    onClose={() => setIsAddPlanModalOpen(false)}
                    onSubmit={(planData) => {
                        if (modalContext.mode === "create-custom") {
                            const customId = `custom-${Date.now()}`;
                            setCustomPeriods((prev) => [
                                ...prev,
                                {
                                    id: customId,
                                    name: planData.title || "Custom Period",
                                    fromDate: planData.fromDate || null,
                                    toDate: planData.toDate || null,
                                    plan: { title: planData.title, timePeriod: "custom", focusItems: [], sections: {} },
                                },
                            ]);
                            setSelectedPlanRef({ type: "custom", key: customId });
                        } else if (selectedPlanRef.type === "static") {
                            setPlansByPeriod((prev) => ({
                                ...prev,
                                [selectedPlanRef.key]: {
                                    title: planData.title,
                                    timePeriod: selectedPlanRef.key,
                                    fromDate: planData.fromDate || null,
                                    toDate: planData.toDate || null,
                                    focusItems: [],
                                    sections: {},
                                },
                            }));
                        }
                        setIsAddPlanModalOpen(false);
                    }}
                    initialTimePeriod={modalContext.period}
                    mode={modalContext.mode}
                />
            )}

            {calendarEntryModalState && (
                <CalendarPlannerEntryModal
                    onClose={() => setCalendarEntryModalState(null)}
                    onSubmit={handleCalendarEntrySubmit}
                    dateLabel={calendarEntryModalState.dateLabel}
                    periodLabel={calendarEntryModalState.periodLabel}
                    initialStartTime={calendarEntryModalState.initialStartTime}
                    initialEndTime={calendarEntryModalState.initialEndTime}
                    tasksList={tasksList}
                    projectsList={projectsList}
                    notesList={notesList}
                    recallCardsList={recallCardsList}
                />
            )}
        </div>

    )
}

export default Planner
